import React, { useCallback, useState } from 'react'
import { connect, useSelector } from 'react-redux'

import { format, parseISO } from 'date-fns'
import { Map } from 'immutable'
import { isEmpty } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'

import { getDisplayDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { Transfer } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import SecondaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/secondaryButton/SecondaryButton'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'

import styles from './paymentHistoryDashboard.module.scss'
import DownloadCSVButton from '../common/components/downloadCSV/DownloadCSVButton'
import ExcludePaymentForm from '../common/components/forms/excludePaymentModal/ExcludePaymentForm'
import RejectPaymentForm from '../common/components/forms/rejectPaymentModal/RejectPaymentForm'
import ReversePaymentForm from '../common/components/forms/reversePaymentModal/ReversePaymentForm'
import PaymentCard from '../common/components/paymentCard/PaymentCard'
import PaymentDetailsModal from '../common/components/paymentDetailsModal/PaymentDetailsModal'
import {
  INVOICE_NUMBER_PER_PAGE,
  paymentExclusionReasons,
  paymentRejectionReasons,
  paymentReversalReasons,
  ROLES,
} from '../common/constants/appConstants'
import { GET_PRACTICE_PAYMENT_HISTORY, GET_PROVIDER_PAYMENT_HISTORY } from '../common/constants/reduxConstants'
import { hasErrorForAction, hasRole } from '../common/utils/utils'
import { getAlertsState } from '../data/alertsSelectors'
import { Alert } from '../data/alertTypes'
import { getAuthRoles } from '../data/auth/authSelectors'
import { AppDispatch, RootState } from '../data/store'
import { resetDAEngageAppointmentAndNote, submitChargeStatusUpdate } from '../payments/data/paymentModalUtils'
import { downloadPayments, updateChargeStatus } from '../payments/data/paymentsDataActions'
import { getPracticePaymentHistory } from '../practices/individualPractice/data/practiceDetailsActions'
import { getPracticeDetails } from '../practices/individualPractice/data/practiceDetailsSelectors'
import { getProvidersDetails } from '../providers/data/providerSelectors'
import { getProviderPaymentHistory } from '../providers/individualProvider/data/providerDetailsActions'

const CONFLICT_STATUSES = ['SUBMITTED', 'PENDING', 'PENDING_NOTE', 'APPROVED']

const getConflictingDates = (payment: Transfer): string[] => {
  const showConflicts = CONFLICT_STATUSES.includes(payment.status.toUpperCase())
  if (
    showConflicts &&
    payment.is_too_frequent_with_charge_ids !== null &&
    payment.is_too_frequent_with_charge_ids.length !== 0 &&
    Array.isArray(payment.is_too_frequent_with_charge_ids)
  ) {
    return payment.is_too_frequent_with_charge_ids.map((charge) =>
      format(parseISO(charge.date_of_service), 'MM/dd/yyyy'),
    )
  }
  return []
}

type PaymentsListProps = {
  paymentsList: Transfer[]
  readonly?: boolean
  onViewClick: (data: Map<string, any>) => void
  onReverseClick: (data: Map<string, any>) => void
  onRejectClick: (data: Map<string, any>) => void
  onExcludeClick: (data: Map<string, any>) => void
}

// Memoized so an unrelated parent re-render (e.g. opening a modal for one row) doesn't
// force every row in a potentially large, unbounded ("Load More") list to re-render.
const PaymentsList = React.memo<PaymentsListProps>(
  ({ paymentsList, readonly, onViewClick, onReverseClick, onRejectClick, onExcludeClick }) => (
    <div>
      {paymentsList.map((payment, i) => (
        <PaymentCard
          readonly={readonly}
          data={payment}
          conflicts={getConflictingDates(payment)}
          type='patient'
          key={`$$historyData${i}`}
          viewClick={onViewClick}
          reverseClick={onReverseClick}
          rejectClick={onRejectClick}
          excludeClick={onExcludeClick}
        />
      ))}
    </div>
  ),
)
PaymentsList.displayName = 'PaymentsList'

const PaymentHistoryDashboard: React.FC<PaymentHistoryDashboardProps> = ({
  isPractice,
  lyraID,
  historyData,
  actions: { getPracticePaymentHistory, getProviderPaymentHistory, downloadPayments, updateChargeStatus },
  dispatch,
}) => {
  const roles = useSelector(getAuthRoles)
  const alerts: Alert[] = useSelector(getAlertsState)
  const [loadingMore, setLoadingMore] = useState(false)
  const [reversePayment, setReversePayment] = useState(false)
  const [rejectPayment, setRejectPayment] = useState(false)
  const [excludePayment, setExcludePayment] = useState(false)
  const [viewPayment, setViewPayment] = useState(false)
  const [modalData, setModalData] = useState<any>({})
  const [noMoreForType, setNoMoreForType] = useState({ all: false })

  const onViewClick = useCallback((data: Map<string, any>) => {
    setReversePayment(false)
    setRejectPayment(false)
    setExcludePayment(false)
    setViewPayment(true)
    setModalData(data)
  }, [])

  const onReverseClick = useCallback((chargeData: Map<string, any>) => {
    setReversePayment(true)
    setRejectPayment(false)
    setExcludePayment(false)
    setViewPayment(false)
    setModalData(chargeData)
  }, [])

  const onRejectClick = useCallback((chargeData: Map<string, any>) => {
    setRejectPayment(true)
    setReversePayment(false)
    setExcludePayment(false)
    setViewPayment(false)
    setModalData(chargeData)
  }, [])

  const onExcludeClick = useCallback((chargeData: Map<string, any>) => {
    setExcludePayment(true)
    setReversePayment(false)
    setRejectPayment(false)
    setViewPayment(false)
    setModalData(chargeData)
  }, [])

  const renderPaymentsList = () => {
    if (!historyData || !('all' in historyData)) {
      const action = isPractice ? GET_PRACTICE_PAYMENT_HISTORY : GET_PROVIDER_PAYMENT_HISTORY
      if (hasErrorForAction(alerts, action)) {
        return false // custom workflow or display could render here
      } else {
        if (isPractice) {
          getPracticePaymentHistory({ practiceID: lyraID, page: 0, limit: INVOICE_NUMBER_PER_PAGE })
        } else {
          getProviderPaymentHistory({ providerID: lyraID, page: 0, limit: INVOICE_NUMBER_PER_PAGE })
        }
        return (
          <div className={styles['loading-container']}>
            <LoadingIndicator size={45} />
          </div>
        )
      }
    }

    const paymentsList = historyData.all.data

    if (paymentsList.length === 0) {
      return <div className={styles['loading-container']}>No payments found.</div>
    }

    return (
      <PaymentsList
        paymentsList={paymentsList}
        readonly={hasRole(roles, ROLES.VIEW_ONLY)}
        onViewClick={onViewClick}
        onReverseClick={onReverseClick}
        onRejectClick={onRejectClick}
        onExcludeClick={onExcludeClick}
      />
    )
  }

  const onCSVDownloadClick = ($$values: Map<string, any>) => {
    const startDate = getDisplayDate($$values.get('start_date'), 'YYYY-MM-DD')
    const endDate = getDisplayDate($$values.get('end_date'), 'YYYY-MM-DD')
    if (isPractice) {
      return downloadPayments($$values.get('payment_status'), startDate, endDate, undefined, lyraID)
    } else {
      return downloadPayments($$values.get('payment_status'), startDate, endDate, lyraID)
    }
  }

  const loadMore = () => {
    const currentPage = historyData?.all?.page
    const nextPage = parseInt(currentPage, 10) + 1

    setLoadingMore(true)
    const action = isPractice
      ? getPracticePaymentHistory({ practiceID: lyraID, page: nextPage, limit: INVOICE_NUMBER_PER_PAGE })
      : getProviderPaymentHistory({ providerID: lyraID, page: nextPage, limit: INVOICE_NUMBER_PER_PAGE })
    action.then(
      (successObj) => {
        const noMores = noMoreForType
        noMores.all = successObj.data.length < INVOICE_NUMBER_PER_PAGE

        setLoadingMore(false)
        setNoMoreForType(noMores)
      },
      () => {
        setLoadingMore(false)
      },
    )
  }

  const submitReversePayment = async (values: Map<string, any>) => {
    await submitChargeStatusUpdate(values, modalData, paymentReversalReasons, updateChargeStatus)
    await resetDAEngageAppointmentAndNote(dispatch, modalData)
    setReversePayment(false)
    return true
  }

  const submitRejectPayment = async (values: Map<string, any>) => {
    await submitChargeStatusUpdate(values, modalData, paymentRejectionReasons, updateChargeStatus)
    setRejectPayment(false)
    return true
  }

  const submitExcludePayment = async (values: Map<string, any>) => {
    await submitChargeStatusUpdate(values, modalData, paymentExclusionReasons, updateChargeStatus)
    setExcludePayment(false)
    return true
  }

  const ModalData = (
    <PaymentDetailsModal
      // @ts-expect-error TS(2322): Type '{ type: string; viewData: any; closeModal: (... Remove this comment to see the full error message
      type='patient'
      viewData={viewPayment ? modalData : {}}
      closeModal={() => setViewPayment(false)}
    />
  )

  // Populate initial values for RejectPaymentForm if there are any
  let initialRejectionValues
  const prevRejectionComment = rejectPayment && modalData ? modalData.admin_note : undefined
  if (prevRejectionComment) {
    if (Object.values(paymentRejectionReasons).includes(prevRejectionComment)) {
      const rejectionReason = Object.keys(paymentRejectionReasons).find(
        (key) => paymentRejectionReasons[key as keyof typeof paymentRejectionReasons] === prevRejectionComment,
      )
      initialRejectionValues = {
        description: rejectionReason,
      }
    } else {
      initialRejectionValues = {
        description: 'other',
        description_text_area: prevRejectionComment,
      }
    }
  }

  let initialReversalValues
  const prevReversalComment = reversePayment && modalData ? modalData.admin_note : undefined
  if (prevReversalComment) {
    if (Object.values(paymentReversalReasons).includes(prevReversalComment)) {
      const reversalReason = Object.keys(paymentReversalReasons).find(
        (key) => paymentReversalReasons[key as keyof typeof paymentReversalReasons] === prevReversalComment,
      )
      initialReversalValues = {
        description: reversalReason,
      }
    } else {
      initialReversalValues = {
        description: 'other',
        description_text_area: prevReversalComment,
      }
    }
  }

  let initialExclusionValues
  const prevExclusionComment = excludePayment && modalData ? modalData.admin_note : undefined
  if (prevExclusionComment) {
    if (Object.values(paymentExclusionReasons).includes(prevExclusionComment)) {
      const exclusionReason = Object.keys(paymentExclusionReasons).find(
        (key) => paymentExclusionReasons[key as keyof typeof paymentExclusionReasons] === prevExclusionComment,
      )
      initialExclusionValues = {
        description: exclusionReason,
      }
    } else {
      initialExclusionValues = {
        description: 'other',
        description_text_area: prevExclusionComment,
      }
    }
  }

  const ReversePaymentModal = (
    // @ts-expect-error TS(2322): Type '{ submitFunction: (values: Map<string, any>)... Remove this comment to see the full error message
    <ReversePaymentForm submitFunction={submitReversePayment} showReverseText initialValues={initialReversalValues} />
  )

  const RejectPaymentModal = (
    // @ts-expect-error TS(2322): Type '{ submitFunction: (values: Map<string, any>)... Remove this comment to see the full error message
    <RejectPaymentForm submitFunction={submitRejectPayment} showRejectText initialValues={initialRejectionValues} />
  )

  const ExcludePaymentModal = (
    // @ts-expect-error TS(2322): Type '{ submitFunction: (values: Map<string, any>)... Remove this comment to see the full error message
    <ExcludePaymentForm submitFunction={submitExcludePayment} showExcludeText initialValues={initialExclusionValues} />
  )

  let modalToShow
  if (reversePayment) {
    modalToShow = (
      <BaseModal isOpen={reversePayment} body={ReversePaymentModal} closeModal={() => setReversePayment(false)} />
    )
  } else if (rejectPayment) {
    modalToShow = (
      <BaseModal isOpen={rejectPayment} body={RejectPaymentModal} closeModal={() => setRejectPayment(false)} />
    )
  } else if (excludePayment) {
    modalToShow = (
      <BaseModal isOpen={excludePayment} body={ExcludePaymentModal} closeModal={() => setExcludePayment(false)} />
    )
  } else {
    modalToShow = <BaseModal isOpen={viewPayment} body={ModalData} closeModal={() => setViewPayment(false)} />
  }

  const hasPayments = !!historyData && 'all' in historyData && !isEmpty(historyData.all.data)
  return (
    <div>
      {hasPayments ? (
        <div className={styles['top-container']}>
          {/* @ts-expect-error TS(2322): Type '($$values: Map<string, any>) => Dispatch<Any... Remove this comment to see the full error message */}
          <DownloadCSVButton downloadClick={onCSVDownloadClick} />
        </div>
      ) : (
        ''
      )}
      {renderPaymentsList()}
      {noMoreForType.all ? (
        <div className={styles['end-of-list-spacer']} />
      ) : (
        <div className={styles['load-more']}>
          {loadingMore ? (
            <LoadingIndicator size={30} />
          ) : hasPayments ? (
            <SecondaryButton onClick={loadMore}>Load More</SecondaryButton>
          ) : (
            []
          )}
        </div>
      )}
      {modalToShow}
    </div>
  )
}

const mapStateToProps = (state: RootState, props: PaymentHistoryDashboardProps) => {
  const details = props.isPractice ? getPracticeDetails(state) : getProvidersDetails(state)
  return {
    lyraID: props.isPractice ? details?.data?.id : (details?.data as any)?.lyra_id,
    historyData: details.paymentHistory,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: bindActionCreators(
    {
      getProviderPaymentHistory,
      getPracticePaymentHistory,
      downloadPayments,
      updateChargeStatus,
    },
    dispatch,
  ),
  dispatch,
})

type PaymentHistoryDashboardProps = {
  isPractice: boolean
  lyraID: string
  historyData: {
    [status: string]: {
      data: Transfer[]
      page: string
    }
  }
  actions: {
    getPracticePaymentHistory: (params: object) => Promise<any>
    getProviderPaymentHistory: (params: object) => Promise<any>
    downloadPayments: (
      status: string,
      start: string,
      end: string,
      providerId?: string,
      practiceId?: string,
    ) => Dispatch<AnyAction>
    updateChargeStatus: (
      id: string,
      values: Map<string, any>,
      isTooFrequent: boolean,
      description?: string,
    ) => Promise<any>
  }
  dispatch: AppDispatch
}

// @ts-expect-error TS(2345): Argument of type 'FC<PaymentHistoryDashboardProps>... Remove this comment to see the full error message
export default connect(mapStateToProps, mapDispatchToProps)(PaymentHistoryDashboard)
